/**
 * src/data/users.js — JWT Session Store
 *
 * Single source of truth for the authenticated session. Session data comes
 * ONLY from the live FastAPI backend (/auth/login). No hardcoded users,
 * no dummy data, no local authentication logic.
 *
 * Shape of a stored session:
 *   { id, email, name, role, token }
 *   where `token` is the raw JWT string returned by the backend.
 */

const SESSION_KEY = "ci_current_user";

/**
 * Decode JWT payload (base64) without verification (client-side only).
 * Used to extract role/email when backend response is incomplete.
 * Returns null on any parse error.
 */
function decodeJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/**
 * Persist the session returned by the backend.
 * Accepts the merged object { ...user, token } or null to clear.
 */
export function setCurrentUser(session) {
  try {
    if (session) {
      window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    } else {
      window.localStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.)
  }
}

/**
 * Return the current session object, or null if nobody is logged in.
 * Callers should treat a null return as "unauthenticated".
 */
export function getCurrentUser() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Clear the session and force the user back to the login page.
 * Called by Logout.jsx and any 401 interceptor.
 */
export function logoutUser() {
  setCurrentUser(null);
}

/**
 * Return true only when a session exists AND the stored JWT has not expired.
 *
 * Client-side expiry is read from the JWT `exp` claim (seconds since epoch).
 * This is NOT a security check — the backend still validates the signature.
 * It is a UX guard: prevents the app from treating an expired token as a valid
 * session and showing pages that will immediately 401 on every API call.
 *
 * Side-effect: if the token is found to be expired, the stale session is
 * cleared from localStorage immediately so subsequent calls start clean.
 */
export function isAuthenticated() {
  const user = getCurrentUser();
  if (!user?.token) return false;

  const payload = decodeJwtPayload(user.token);

  // Reject tokens that are not valid JWTs (missing payload, wrong format)
  if (!payload) {
    setCurrentUser(null);
    return false;
  }

  // Reject tokens whose `exp` claim is in the past
  if (payload.exp && payload.exp * 1000 < Date.now()) {
    setCurrentUser(null);
    return false;
  }

  return true;
}

/**
 * Return the role from the session. If the backend didn't include it,
 * attempts to decode the JWT payload for a "role" claim.
 * Returns "admin" | "driver" | "dispatcher" | null.
 */
export function getCurrentRole() {
  const user = getCurrentUser();
  if (!user) return null;
  if (user.role) return user.role;
  const payload = decodeJwtPayload(user.token);
  return payload?.role ?? null;
}
